import { useForm } from 'react-hook-form';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ParseKeys } from 'i18next';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { z } from 'zod';
import { useWithdraw, ZepApiError, ZepApiStatusType } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel, DialogTitle, Input, TextField } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
const withdrawInputSchema = z.object({
  withdrawInput: z.string().regex(/^DELETE$/, 'accounts:accounts.withdrawModal.validationText')
});
type WithdrawInput = z.infer<typeof withdrawInputSchema>;
const DELETE_PHRASE = 'DELETE';
const WithdrawInputModalInner = ({
  onSuccessWithdraw
}: {
  onSuccessWithdraw: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'accounts']);
  const router = useRouter();
  const {
    visible,
    remove,
    hide
  } = useModal();
  const dialog = useDialog();
  const {
    formState: {
      errors,
      isValid
    },
    register
  } = useForm<WithdrawInput>({
    resolver: zodResolver(withdrawInputSchema),
    defaultValues: {
      withdrawInput: ''
    }
  });
  const {
    mutateAsync: mutateWithdraw
  } = useWithdraw();
  const handleConfirmWithdraw = async () => {
    if (!isValid) return;
    hide();
    try {
      const res = await mutateWithdraw();
      if (res.status === 0) {
        onSuccessWithdraw();
      }
    } catch (error) {
      if (error instanceof ZepApiError) {
        if (error.status === ZepApiStatusType.NOT_ALLOWED_TO_DELETE_ACTIVE_PLAN) {
          await dialog.confirm({
            title: t('accounts:profile.accounts.validateWithdrawTitle'),
            content: t('accounts:profile.accounts.validateWithdrawContent'),
            confirmText: t('accounts:profile.accounts.validateWithdrawConfirmText'),
            confirmAction: async () => {
              await router.push('/me/spaces/plans');
            }
          });
          return;
        }
      }
      await dialog.alert({
        title: 'UNKNOWN ERROR',
        content: t('common:error.api.unknown')
      });
    }
  };
  return <Dialog open={visible} onClose={() => null} afterClose={remove} maxWidth={'320px'} data-sentry-element="Dialog" data-sentry-component="WithdrawInputModalInner" data-sentry-source-file="WithdrawInputModal.tsx">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="WithdrawInputModal.tsx">
        <DialogHeader topSlot={'😥'} data-sentry-element="DialogHeader" data-sentry-source-file="WithdrawInputModal.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="WithdrawInputModal.tsx">
            {t('accounts:accounts.withdrawModal.Title')}
          </DialogTitle>
        </DialogHeader>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="WithdrawInputModal.tsx">
          <TextField error={Boolean(errors.withdrawInput)} helperText={t(errors.withdrawInput?.message as ParseKeys<['common', 'accounts']>)} label={<span className="text-[14px] font-[400] leading-[20px] text-gray-900">
                <Trans t={t} i18nKey="accounts:accounts.withdrawModal.content" values={{
            deletePhrase: DELETE_PHRASE
          }} components={{
            Red: <span style={{
              color: Colors.RedDefault
            }} />
          }} />
              </span>} data-sentry-element="TextField" data-sentry-source-file="WithdrawInputModal.tsx">
            <Input isFullWidth {...register('withdrawInput')} placeholder={DELETE_PHRASE} error={Boolean(errors.withdrawInput)} data-sentry-element="Input" data-sentry-source-file="WithdrawInputModal.tsx" />
          </TextField>
        </DialogContent>
        <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="WithdrawInputModal.tsx">
          <Button variant={'sub'} onClick={() => remove()} data-sentry-element="Button" data-sentry-source-file="WithdrawInputModal.tsx">
            {t('accounts:myApp.update.deleteConfirmCancel')}
          </Button>
          <Button variant={'alert'} disabled={!isValid} onClick={handleConfirmWithdraw} data-sentry-element="Button" data-sentry-source-file="WithdrawInputModal.tsx">
            {t('accounts:accounts.withdrawModal.confirmText')}
          </Button>
        </DialogFooter>
      </DialogPanel>
    </Dialog>;
};
export const WithdrawInputModal = NiceModal.create(WithdrawInputModalInner);